// Portal › Kullanıcılar — sağ panel "DÜZENLE" (M16 T20, mockup 16, E2; portal yetki v2 Dalga 3 §8.1):
// set kutusu + rol → "Tüm kategoriler" → üç durumlu kategori tablosu (Gizli/Görüntüle/Düzenle,
// native radio) → "Gösterge istisnaları (n)" → "İşletmeler"; form davetleri (ayrı "Davet gönder"
// düğmesi; Admin'e kapalı), "Hesabı kapat" (ConfirmDialog; son Süper Admin disabled + neden).
// Kaydet → PUT kpi-access: grant listesi + `expected` jetonları (yazılan/silinen HER set). 409
// KPI_ACCESS_STALE → sakin mesaj + güncel hal yeniden yüklenir (sessiz yeniden deneme YOK).
// Kısıtlı Admin: NULL alanlar kilitli, kapsam dışı yalnız sayı, `role` hiç gönderilmez. Taslak
// modeli lib/client-access-draft-v2.ts (danışman kartıyla ortak).

import { useEffect, useRef, useState } from "react";
import type { ClientAdminScope, ClientAdminSet, ClientEntity, ClientUser } from "@mahir/shared";
import { setKpiAccess } from "../../api/client-admin";
import { CLIENT_ADMIN_COPY as C } from "../../lib/client-admin-copy";
import { describeAccessError } from "../../lib/client-access-errors";
import {
  type AccessDraftV2,
  accessLocks,
  buildExpected,
  draftMapFromAccessV2,
  draftsToGrants,
  entitiesWouldBeEmpty,
  newSetDraftV2,
  patchSetDraft,
  toggleSetInDraftV2,
} from "../../lib/client-access-draft-v2";
import { AccessSetEditor } from "./AccessSetEditor";

interface Props {
  clientId: string;
  user: ClientUser;
  sets: ClientAdminSet[];
  entities: ClientEntity[];
  /** Kısıtlı Admin kapsamı; Süper Admin / tam Admin için null. */
  scope: ClientAdminScope | null;
  isSelf: boolean;
  activeSuperAdminCount: number;
  /** Süper Admin: yönetici olan kullanıcıların erişimini düzenleyebilir; Admin yalnız kendininkini (§5). */
  canEditManagers: boolean;
  /** Proje form daveti yalnız Süper Admin'de (§12/2). */
  canInviteToProject: boolean;
  onSaved: (user: ClientUser) => void;
  /** 409 KPI_ACCESS_STALE sonrası güncel kullanıcıyı sessizce yeniden yükle. */
  onStale: () => Promise<void>;
  onInviteToProject: () => void;
  onDeactivate: () => void;
  onClose: () => void;
}

export function ClientUserAccessDrawer(props: Props): JSX.Element {
  const { clientId, user, sets, entities, scope, isSelf, onSaved, onClose } = props;
  const [draft, setDraft] = useState<AccessDraftV2>(() => draftMapFromAccessV2(user.kpiAccess));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  // 409 STALE sonrası güncel kullanıcı gelince taslak sıfırlanır ama sakin mesaj kalır.
  const keepMsg = useRef(false);

  useEffect(() => {
    setDraft(draftMapFromAccessV2(user.kpiAccess));
    if (keepMsg.current) keepMsg.current = false;
    else setMsg(null);
  }, [user]);

  const originals = new Map(user.kpiAccess.map((a) => [a.setId, a]));
  const readOnlyManager = user.adminRole !== null && !props.canEditManagers && !isSelf;
  const closed = user.status === "closed" || readOnlyManager;
  const lastSuperBlock =
    user.adminRole === "super_admin" && !closed && props.activeSuperAdminCount <= 1;
  const deactivateReason = readOnlyManager
    ? C.drawer.scopeLocked.managerReadOnly
    : closed
      ? C.drawer.closedReason
      : lastSuperBlock
        ? C.admins.lastSuper
        : null;
  const emptyEntities = [...draft.values()].some((d) =>
    entitiesWouldBeEmpty(
      d,
      originals.get(d.setId),
      accessLocks(originals.get(d.setId), scope).lockedNullEntities,
    ),
  );

  async function handleSave(): Promise<void> {
    setSaving(true);
    setMsg(null);
    try {
      const grants = draftsToGrants(draft, originals, scope);
      const res = await setKpiAccess(clientId, user.id, grants, buildExpected(grants, originals));
      const materialized = res.materializedSetIds.length > 0;
      setMsg({
        kind: "ok",
        text: materialized ? `${C.drawer.saved}. ${C.drawer.materializedNote}` : C.drawer.saved,
      });
      // X1-m2: açık listeye dönüş notu yeni kullanıcı DTO'su gelince silinmesin
      if (materialized) keepMsg.current = true;
      onSaved(res.user);
    } catch (err) {
      const info = describeAccessError(err, C.drawer.saveFailed);
      setMsg({ kind: "err", text: info.text });
      if (info.reload) {
        keepMsg.current = true;
        await props.onStale().catch(() => {
          keepMsg.current = false;
        });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <aside
      aria-label={`${C.drawer.kicker} ${user.fullName}`}
      className="rounded-xl p-5 flex flex-col gap-4"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        minWidth: 0,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div
            style={{
              fontSize: "0.7rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--color-primary)",
              fontWeight: 700,
            }}
          >
            {C.drawer.kicker}
          </div>
          <h2 className="text-lg" style={{ fontFamily: "var(--font-display)" }}>
            {user.fullName}
          </h2>
          <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
            {user.email}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={C.drawer.close}
          className="text-sm px-2 py-1 rounded"
          style={{ color: "var(--color-text-3)" }}
        >
          ✕
        </button>
      </div>

      {readOnlyManager ? (
        <p
          role="note"
          data-testid="manager-readonly-note"
          className="rounded-md px-3 py-2 text-xs"
          style={{
            background: "var(--color-warning-bg)",
            color: "var(--color-warning-text)",
            border: "1px solid var(--color-warning-border)",
          }}
        >
          {C.drawer.scopeLocked.managerReadOnly}
        </p>
      ) : null}

      {/* KPI setleri — her set: rol, kategori tablosu, istisnalar, işletmeler */}
      <fieldset style={{ minWidth: 0 }}>
        <legend className="text-sm mb-1" style={{ color: "var(--color-text-2)", fontWeight: 600 }}>
          {C.drawer.sets}
        </legend>
        {sets.length === 0 ? (
          <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
            {C.drawer.noSets}
          </div>
        ) : (
          sets.map((s) => (
            <AccessSetEditor
              key={s.id}
              set={s}
              draft={draft.get(s.id)}
              original={originals.get(s.id)}
              entities={entities}
              scope={scope}
              disabled={saving || closed}
              selfAuditorNote={isSelf}
              onToggle={(on) =>
                setDraft((d) =>
                  toggleSetInDraftV2(d, s.id, on, () => newSetDraftV2(s, scope, entities)),
                )
              }
              onPatch={(patch) => setDraft((d) => patchSetDraft(d, s.id, patch))}
            />
          ))
        )}
      </fieldset>
      {user.outOfScopeAccess || user.hiddenSetCount > 0 ? (
        <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {user.hiddenSetCount > 0
            ? C.access.hiddenSets(user.hiddenSetCount)
            : C.access.outOfScopeMark}
        </p>
      ) : null}
      <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
        {C.drawer.narrowNote}
      </p>

      {/* Form davetleri */}
      <section>
        <h3 className="text-sm mb-1" style={{ color: "var(--color-text-2)", fontWeight: 600 }}>
          {C.drawer.forms}
        </h3>
        {user.projects.length === 0 ? (
          <div className="text-xs mb-2" style={{ color: "var(--color-text-3)" }}>
            {C.forms.none}
          </div>
        ) : (
          <ul className="flex flex-col gap-1 mb-2">
            {user.projects.map((p) => (
              <li key={p.projectId} className="text-sm flex items-center gap-2">
                <span>{p.label}</span>
                <span
                  className="px-2 py-0.5 rounded-full text-xs"
                  style={{
                    background: "color-mix(in srgb, var(--color-primary) 10%, white)",
                    color: "var(--color-primary)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  {C.forms.status[p.participantStatus] ?? p.participantStatus}
                </span>
              </li>
            ))}
          </ul>
        )}
        {props.canInviteToProject ? (
          <button
            type="button"
            onClick={props.onInviteToProject}
            disabled={closed}
            className="text-sm disabled:opacity-50"
            style={{ color: "var(--color-primary)", fontWeight: 600 }}
          >
            {C.drawer.inviteToProject}
          </button>
        ) : null}
      </section>

      {msg ? (
        <div
          role={msg.kind === "err" ? "alert" : "status"}
          className="rounded-md px-3 py-2 text-sm"
          style={{
            background:
              msg.kind === "err"
                ? "color-mix(in srgb, var(--color-error) 10%, white)"
                : "color-mix(in srgb, var(--color-success) 12%, white)",
            color: msg.kind === "err" ? "var(--color-error)" : "var(--color-success)",
          }}
        >
          {msg.text}
        </div>
      ) : null}

      {readOnlyManager ? null : (
        <div className="flex items-center justify-end gap-3 mt-auto pt-2">
          <button
            type="button"
            onClick={props.onDeactivate}
            disabled={deactivateReason !== null || saving}
            title={deactivateReason ?? undefined}
            className="text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: "var(--color-error)", fontWeight: 600 }}
          >
            {C.drawer.deactivate}
            {isSelf ? ` (${C.youTag})` : ""}
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || closed || emptyEntities}
            className="px-4 py-2 rounded-lg text-sm disabled:opacity-50"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            {saving ? C.drawer.saving : C.drawer.save}
          </button>
        </div>
      )}
      {deactivateReason ? (
        <p className="text-xs text-right" style={{ color: "var(--color-text-3)" }}>
          {deactivateReason}
        </p>
      ) : null}
    </aside>
  );
}
